import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  ChevronRight, 
  ChevronLeft,
  CheckCircle2,
  Loader2,
  Rocket,
  Clock,
  Sparkles,
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to publish height to parent iframe
const publishHeight = () => {
  const height = document.documentElement.scrollHeight;
  window.parent.postMessage({ type: 'resize', height }, '*');
};

// Helper to notify parent to scroll to top
const scrollParentToTop = () => {
  window.parent.postMessage({ type: 'scrollToTop' }, '*');
};

// Plan options
const planOptions = [
  { 
    id: 'basic', 
    name: 'Basic', 
    price: '$59/mo',
    description: 'For solo agents who don\'t need a website or funnel—just powerful quoting tools'
  },
  { 
    id: 'starter', 
    name: 'Starter', 
    price: '$97/mo',
    description: 'For independent agents with a single website who want to start capturing leads online'
  },
  { 
    id: 'professional', 
    name: 'Professional', 
    price: '$199/mo',
    description: 'For growth-minded agents or small teams ready for full white-labeling and CRM integrations'
  },
  { 
    id: 'team', 
    name: 'Agency', 
    price: '$399/mo',
    description: 'For agencies managing multiple agents and brands with full resell capabilities'
  },
];

// Agency size options
const agencySizeOptions = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5 people' },
  { value: '6-10', label: '6-10 people' },
  { value: '11-25', label: '11-25 people' },
  { value: '26-50', label: '26-50 people' },
  { value: '50+', label: '50+ people' },
];

// States licensed options
const statesLicensedOptions = [
  { value: '1-5', label: '1-5 states' },
  { value: '6-10', label: '6-10 states' },
  { value: '11-25', label: '11-25 states' },
  { value: '26-40', label: '26-40 states' },
  { value: '41-50', label: '41-50 states' },
  { value: 'all', label: 'All 50 states' },
];

// Ad platforms
const adPlatforms = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'google', label: 'Google' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'twitter', label: 'Twitter/X' },
  { id: 'other', label: 'Other' },
];

// Phone number formatter
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  if (phoneNumber.length < 4) return phoneNumber;
  if (phoneNumber.length < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

// Validation schemas
const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .regex(/^[\d\s\-\(\)\+]+$/, 'Please enter a valid phone number'),
  company_name: z.string().min(2, 'Company name must be at least 2 characters'),
});

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

// Loading skeleton component
const FormSkeleton = () => (
  <div className="min-h-fit bg-transparent py-8 px-4 animate-pulse">
    <div className="max-w-full w-full" style={{ maxWidth: '100%' }}>
      {/* Header skeleton */}
      <div className="text-center mb-8">
        <div className="h-6 bg-slate-200 rounded-full w-48 mx-auto mb-4"></div>
        <div className="h-8 bg-slate-200 rounded-lg w-56 mx-auto mb-3"></div>
        <div className="h-4 bg-slate-200 rounded w-80 mx-auto"></div>
      </div>

      {/* Progress bar skeleton */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          <div className="h-4 bg-slate-200 rounded w-20"></div>
          <div className="h-4 bg-slate-200 rounded w-24"></div>
        </div>
        <div className="h-2 bg-slate-200 rounded-full"></div>
      </div>

      {/* Form card skeleton */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
        <div className="text-center mb-8">
          <div className="h-4 bg-slate-200 rounded w-16 mx-auto mb-3"></div>
          <div className="h-7 bg-slate-200 rounded w-64 mx-auto mb-2"></div>
          <div className="h-4 bg-slate-200 rounded w-48 mx-auto"></div>
        </div>
        
        {/* Options skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-4 rounded-xl border-2 border-slate-200">
              <div className="flex justify-between items-start mb-2">
                <div className="h-5 bg-slate-200 rounded w-24"></div>
                <div className="h-5 bg-slate-200 rounded w-16"></div>
              </div>
              <div className="h-4 bg-slate-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const WaitlistFormWidget = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  
  // Form data state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [hasWebsite, setHasWebsite] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [usesCRM, setUsesCRM] = useState(null);
  const [crmName, setCrmName] = useState('');
  const [agencySize, setAgencySize] = useState('');
  const [statesLicensed, setStatesLicensed] = useState('');
  const [runsPaidAds, setRunsPaidAds] = useState(null);
  const [adPlatformsSelected, setAdPlatformsSelected] = useState([]);
  const [otherPlatform, setOtherPlatform] = useState('');
  const [contactData, setContactData] = useState({});

  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

  const contactForm = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company_name: '',
    },
  });

  // Set up iframe height communication with parent
  useEffect(() => {
    // Suppress body scrollbar and set transparent background for iframe embedding
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.background = 'transparent';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.background = 'transparent';

    // Short delay to ensure DOM is ready, then show content and publish height
    const initTimeout = setTimeout(() => {
      setIsLoading(false);
      requestAnimationFrame(() => {
        publishHeight();
      });
    }, 150);

    // Observe body resize (step transitions, validation messages, etc.)
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(publishHeight);
      resizeObserver.observe(document.body);
    }

    // Also publish on window resize for safety
    window.addEventListener('resize', publishHeight);

    return () => {
      clearTimeout(initTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', publishHeight);
    };
  }, []);

  // Publish height whenever step or submission state changes
  useEffect(() => {
    const timeout = setTimeout(publishHeight, 100);
    return () => clearTimeout(timeout);
  }, [currentStep, submissionSuccess, isLoading]);

  // Handle step navigation with scroll to top for parent
  const goToNextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
    scrollParentToTop();
  }, []);

  const goToPrevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
    scrollParentToTop();
  }, []);

  const handlePlatformToggle = (platformId) => {
    setAdPlatformsSelected(prev => 
      prev.includes(platformId) 
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  const handleContactSubmit = async (data) => {
    setContactData(data);
    setIsSubmitting(true);
    
    // Combine ad platforms with custom "other" entry
    let platforms = runsPaidAds ? [...adPlatformsSelected] : [];
    if (adPlatformsSelected.includes('other') && otherPlatform) {
      platforms = platforms.filter(p => p !== 'other');
      platforms.push(`Other: ${otherPlatform}`);
    }
    
    const submissionData = {
      ...data,
      plan_interest: selectedPlan,
      has_website: hasWebsite,
      website_url: hasWebsite ? websiteUrl : null,
      uses_crm: usesCRM,
      crm_name: usesCRM ? crmName : null,
      agency_size: agencySize,
      states_licensed: statesLicensed,
      runs_paid_ads: runsPaidAds,
      ad_platforms: platforms,
    };

    try {
      await axios.post(`${API}/waitlist`, submissionData);
      setSubmissionSuccess(true);
      scrollParentToTop();
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedStep1 = selectedPlan !== null;
  const canProceedStep2 = hasWebsite !== null && usesCRM !== null && agencySize && statesLicensed;
  const canProceedStep3 = runsPaidAds !== null && (runsPaidAds === false || adPlatformsSelected.length > 0);

  // Show loading skeleton while initializing
  if (isLoading) {
    return <FormSkeleton />;
  }

  return (
    <div className="min-h-fit bg-transparent py-8 px-4">
      <div className="max-w-full w-full" style={{ maxWidth: '100%' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Clock className="w-4 h-4" />
            Exciting Updates Coming Soon
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Join the Waitlist
          </h1>
          <p className="text-slate-600 text-base max-w-xl mx-auto">
            We're making major improvements to Quoteplicity! New sign-ups will join our waitlist for the upgraded platform.
          </p>
        </motion.div>

        {/* Benefits - Only on Step 1 */}
        {currentStep === 1 && !submissionSuccess && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
          >
            <div className="flex items-center gap-3 bg-[#e6faf7] p-4 rounded-xl">
              <Rocket className="w-8 h-8 text-[#10c0a5]" />
              <div>
                <p className="font-medium text-slate-900">First Access</p>
                <p className="text-sm text-slate-600">Launch in order of signup</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-[#e6faf7] p-4 rounded-xl">
              <Sparkles className="w-8 h-8 text-[#10c0a5]" />
              <div>
                <p className="font-medium text-slate-900">Better Features</p>
                <p className="text-sm text-slate-600">Enhanced functionality</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-[#e6faf7] p-4 rounded-xl">
              <CheckCircle2 className="w-8 h-8 text-[#10c0a5]" />
              <div>
                <p className="font-medium text-slate-900">New Platform</p>
                <p className="text-sm text-slate-600">Easier installation</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Progress Bar */}
        {!submissionSuccess && (
          <div className="mb-8">
            <div className="flex justify-between text-sm text-slate-500 mb-2">
              <span>Step {currentStep} of {totalSteps}</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="h-2 bg-slate-100" />
          </div>
        )}

        {/* Main Card */}
        <motion.div
          className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <AnimatePresence mode="wait">
            {/* Step 1: Plan Selection */}
            {currentStep === 1 && !submissionSuccess && (
              <motion.div
                key="step1"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="p-4 sm:p-8"
                data-testid="waitlist-step-1"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 1</span>
                  <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Which plan interests you?
                  </h2>
                  <p className="text-slate-600">Select the plan that best fits your needs</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {planOptions.map((plan) => (
                    <button
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`p-4 sm:p-6 rounded-xl border-2 text-left transition-all ${
                        selectedPlan === plan.id
                          ? 'border-[#10c0a5] bg-[#e6faf7]'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`plan-${plan.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-slate-900">{plan.name}</span>
                        <span className="text-[#10c0a5] font-bold">{plan.price}</span>
                      </div>
                      <p className="text-sm text-slate-600">{plan.description}</p>
                    </button>
                  ))}
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={goToNextStep}
                    disabled={!canProceedStep1}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                      canProceedStep1
                        ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    Continue
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Business Info */}
            {currentStep === 2 && !submissionSuccess && (
              <motion.div
                key="step2"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="p-4 sm:p-8"
                data-testid="waitlist-step-2"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 2</span>
                  <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Tell us about your business
                  </h2>
                  <p className="text-slate-600">Help us understand your current setup</p>
                </div>

                <div className="space-y-6">
                  {/* Website Question */}
                  <div>
                    <Label className="text-base font-medium text-slate-900 mb-3 block">
                      Do you currently have a website?
                    </Label>
                    <div className="flex gap-4">
                      <button
                        onClick={() => setHasWebsite(true)}
                        className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                          hasWebsite === true
                            ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                            : 'border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => { setHasWebsite(false); setWebsiteUrl(''); }}
                        className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                          hasWebsite === false
                            ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                            : 'border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        No
                      </button>
                    </div>
                    {hasWebsite && (
                      <div className="mt-3">
                        <Input
                          type="url"
                          placeholder="https://yourwebsite.com"
                          value={websiteUrl}
                          onChange={(e) => setWebsiteUrl(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    )}
                  </div>

                  {/* CRM Question */}
                  <div>
                    <Label className="text-base font-medium text-slate-900 mb-3 block">
                      Do you currently use a CRM?
                    </Label>
                    <div className="flex gap-4">
                      <button
                        onClick={() => setUsesCRM(true)}
                        className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                          usesCRM === true
                            ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                            : 'border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => { setUsesCRM(false); setCrmName(''); }}
                        className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                          usesCRM === false
                            ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                            : 'border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        No
                      </button>
                    </div>
                    {usesCRM && (
                      <div className="mt-3">
                        <Input
                          type="text"
                          placeholder="Which CRM do you use?"
                          value={crmName}
                          onChange={(e) => setCrmName(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    )}
                  </div>

                  {/* Agency Size */}
                  <div>
                    <Label className="text-base font-medium text-slate-900 mb-3 block">
                      How many people are in your agency?
                    </Label>
                    <Select value={agencySize} onValueChange={setAgencySize}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select agency size" />
                      </SelectTrigger>
                      <SelectContent>
                        {agencySizeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* States Licensed */}
                  <div>
                    <Label className="text-base font-medium text-slate-900 mb-3 block">
                      How many states are you licensed in?
                    </Label>
                    <Select value={statesLicensed} onValueChange={setStatesLicensed}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select number of states" />
                      </SelectTrigger>
                      <SelectContent>
                        {statesLicensedOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <button
                    onClick={goToPrevStep}
                    className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={goToNextStep}
                    disabled={!canProceedStep2}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                      canProceedStep2
                        ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    Continue
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Advertising */}
            {currentStep === 3 && !submissionSuccess && (
              <motion.div
                key="step3"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="p-4 sm:p-8"
                data-testid="waitlist-step-3"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 3</span>
                  <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Tell us about your advertising
                  </h2>
                  <p className="text-slate-600">Help us understand your marketing efforts</p>
                </div>

                <div className="space-y-6">
                  {/* Paid Ads Question */}
                  <div>
                    <Label className="text-base font-medium text-slate-900 mb-3 block">
                      Do you currently run paid ads?
                    </Label>
                    <div className="flex gap-4">
                      <button
                        onClick={() => setRunsPaidAds(true)}
                        className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                          runsPaidAds === true
                            ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                            : 'border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => { setRunsPaidAds(false); setAdPlatformsSelected([]); setOtherPlatform(''); }}
                        className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                          runsPaidAds === false
                            ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                            : 'border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>

                  {/* Ad Platforms */}
                  {runsPaidAds && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                    >
                      <Label className="text-base font-medium text-slate-900 mb-3 block">
                        Which platforms do you advertise on? (Select all that apply)
                      </Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {adPlatforms.map((platform) => (
                          <div key={platform.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={platform.id}
                              checked={adPlatformsSelected.includes(platform.id)}
                              onCheckedChange={() => handlePlatformToggle(platform.id)}
                            />
                            <label
                              htmlFor={platform.id}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {platform.label}
                            </label>
                          </div>
                        ))}
                      </div>
                      {adPlatformsSelected.includes('other') && (
                        <div className="mt-3">
                          <Input
                            type="text"
                            placeholder="Please specify other platform(s)"
                            value={otherPlatform}
                            onChange={(e) => setOtherPlatform(e.target.value)}
                            className="w-full"
                          />
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>

                <div className="flex justify-between mt-8">
                  <button
                    onClick={goToPrevStep}
                    className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={goToNextStep}
                    disabled={!canProceedStep3}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                      canProceedStep3
                        ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    Continue
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Contact Info */}
            {currentStep === 4 && !submissionSuccess && (
              <motion.div
                key="step4"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="p-4 sm:p-8"
                data-testid="waitlist-step-4"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 4</span>
                  <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Your contact information
                  </h2>
                  <p className="text-slate-600">We'll use this to reach out when your spot is ready</p>
                </div>

                <form onSubmit={contactForm.handleSubmit(handleContactSubmit)} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-2 block">
                      Your Name *
                    </Label>
                    <Input
                      id="name"
                      {...contactForm.register('name')}
                      placeholder="John Smith"
                      className={contactForm.formState.errors.name ? 'border-red-300' : ''}
                    />
                    {contactForm.formState.errors.name && (
                      <p className="mt-1 text-sm text-red-500">{contactForm.formState.errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="company_name" className="text-sm font-medium text-slate-700 mb-2 block">
                      Company/Agency Name *
                    </Label>
                    <Input
                      id="company_name"
                      {...contactForm.register('company_name')}
                      placeholder="Your Insurance Agency"
                      className={contactForm.formState.errors.company_name ? 'border-red-300' : ''}
                    />
                    {contactForm.formState.errors.company_name && (
                      <p className="mt-1 text-sm text-red-500">{contactForm.formState.errors.company_name.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700 mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      {...contactForm.register('phone')}
                      placeholder="(555) 555-5555"
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        contactForm.setValue('phone', formatted);
                      }}
                      className={contactForm.formState.errors.phone ? 'border-red-300' : ''}
                    />
                    {contactForm.formState.errors.phone && (
                      <p className="mt-1 text-sm text-red-500">{contactForm.formState.errors.phone.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      {...contactForm.register('email')}
                      placeholder="you@example.com"
                      className={contactForm.formState.errors.email ? 'border-red-300' : ''}
                    />
                    {contactForm.formState.errors.email && (
                      <p className="mt-1 text-sm text-red-500">{contactForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="flex justify-between pt-4">
                    <button
                      type="button"
                      onClick={goToPrevStep}
                      className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center gap-2 px-8 py-3 bg-[#10c0a5] hover:bg-[#0ea892] text-white rounded-xl font-medium transition-all disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Join Waitlist
                          <ChevronRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Success State */}
            {submissionSuccess && (
              <motion.div
                key="success"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                className="p-4 sm:p-8 text-center"
                data-testid="waitlist-success"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#e6faf7] mb-6"
                >
                  <CheckCircle2 className="w-10 h-10 text-[#10c0a5]" />
                </motion.div>

                <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  You're on the Waitlist!
                </h2>
                <p className="text-slate-600 text-lg mb-6 max-w-md mx-auto">
                  Thank you for your interest in Quoteplicity! We'll contact you as soon as 
                  a spot opens up on our new platform.
                </p>

                <div className="bg-slate-50 rounded-xl p-6 max-w-md mx-auto text-left">
                  <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
                  <ul className="space-y-2 text-slate-600 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                      <span>You'll receive a confirmation email shortly</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                      <span>We'll notify you when your spot is ready</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                      <span>New clients launch in order of signup</span>
                    </li>
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8 text-slate-500 text-sm"
        >
          <p>
            Questions? Contact us at{' '}
            <a href="mailto:support@quoteplicity.com" className="text-[#10c0a5] hover:underline font-medium">
              support@quoteplicity.com
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default WaitlistFormWidget;
